import { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, User, Pencil, DollarSign, TrendingUp, AlertTriangle, Play, Trash2 } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type CollisionDetection,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProject, useProjectHours, useUpdateProject, useDeleteProject } from "@/hooks/useProjects";
import { useProjectTimeStats } from "@/hooks/useProjectTimeStats";
import { useUpdateProjectWithPayments } from "@/hooks/useProjectWithPayments";
import { useClients } from "@/hooks/useClients";
import { useTasks, useCreateTask, useUpdateTask, useDeleteTask } from "@/hooks/useTasks";
import { useTimeEntries } from "@/hooks/useTimeEntries";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useProjectPaymentSummary } from "@/hooks/usePayments";
import { ProjectPaymentsSection } from "@/components/payments/ProjectPaymentsSection";
import { ProjectManualTimeEntry } from "@/components/projects/ProjectManualTimeEntry";
import { PaymentSetupSection, type PaymentSetupData } from "@/components/projects/PaymentSetupSection";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow, differenceInDays, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { KanbanColumn } from "@/components/kanban/KanbanColumn";
import { SortableTaskCard } from "@/components/kanban/SortableTaskCard";
import { TaskDragOverlay } from "@/components/kanban/DragOverlay";
import type { Database } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";

type ProjectStatus = Database["public"]["Enums"]["project_status"];
type BillingType = "hourly" | "fixed";

const statusColors = {
  backlog: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/10 text-primary",
  review: "bg-warning/10 text-warning",
  done: "bg-success/10 text-success",
};

const statusLabels = {
  backlog: "Backlog",
  in_progress: "Em Andamento",
  review: "Revisão",
  done: "Concluído",
};

const taskColumns = [
  { id: "todo", title: "A Fazer" },
  { id: "doing", title: "Em Progresso" },
  { id: "done", title: "Concluído" },
];

type Task = {
  id: string;
  title: string;
  description?: string | null;
  status?: string | null;
  completed?: boolean | null;
};

const statusOptions: { id: ProjectStatus; title: string }[] = [
  { id: "backlog", title: "Backlog" },
  { id: "in_progress", title: "Em Andamento" },
  { id: "review", title: "Revisão" },
  { id: "done", title: "Concluído" },
];

interface ProjectFormData {
  name: string;
  description: string;
  client_id: string;
  deadline: string;
  status: ProjectStatus;
  billing_type: BillingType;
  hourly_rate: string;
  estimated_hours: string;
  fixed_price: string;
  completion_percentage: number;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const { data: project, isLoading: projectLoading } = useProject(id || "");
  const { data: projectHours } = useProjectHours(id || "");
  const { data: tasks, isLoading: tasksLoading } = useTasks(id || "");
  const { data: allTimeEntries } = useTimeEntries();
  const { data: clients } = useClients();
  const { data: profile } = useProfile();
  const { data: paymentSummary } = useProjectPaymentSummary(id || "");

  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const updateProjectWithPayments = useUpdateProjectWithPayments();

  const [newTask, setNewTask] = useState("");
  const [addingToColumn, setAddingToColumn] = useState<string | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [syncPayments, setSyncPayments] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    description: "",
    client_id: "",
    deadline: "",
    status: "backlog",
    billing_type: "hourly",
    hourly_rate: "",
    estimated_hours: "",
    fixed_price: "",
    completion_percentage: 0,
  });

  const defaultPaymentSetup = (): PaymentSetupData => ({
    createPayments: true,
    paymentPlan: 'full',
    installmentConfig: {
      count: 3,
      intervalDays: 30,
      firstDueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    },
    recurringConfig: {
      dayOfMonth: new Date().getDate(),
      firstDueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    },
  });

  const [paymentSetup, setPaymentSetup] = useState<PaymentSetupData>(defaultPaymentSetup());

  // Check if there are pending payments
  const hasPendingPayments = (paymentSummary?.count_pending || 0) + (paymentSummary?.count_overdue || 0) > 0;

  // Populate form when project loads or dialog opens
  useEffect(() => {
    if (project && isEditDialogOpen) {
      setFormData({
        name: project.name || "",
        description: project.description || "",
        client_id: project.client_id || "",
        deadline: project.deadline || "",
        status: project.status || "backlog",
        billing_type: ((project as any).billing_type as BillingType) || "hourly",
        hourly_rate: project.hourly_rate?.toString() || "",
        estimated_hours: project.estimated_hours?.toString() || "",
        fixed_price: (project as any).fixed_price?.toString() || "",
        completion_percentage: (project as any).completion_percentage || 0,
      });
      setSyncPayments(false);
      setPaymentSetup(defaultPaymentSetup());
    }
  }, [project, isEditDialogOpen]);

  const handleSaveProject = async () => {
    if (!id || !formData.name.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome do projeto é obrigatório.",
      });
      return;
    }

    // Validate based on billing type
    if (formData.billing_type === "hourly") {
      if (!formData.estimated_hours || parseFloat(formData.estimated_hours) <= 0) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Projetos por hora precisam ter horas estimadas maiores que 0.",
        });
        return;
      }
    } else {
      if (!formData.fixed_price || parseFloat(formData.fixed_price) <= 0) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Projetos de preço fixo precisam ter valor definido maior que 0.",
        });
        return;
      }
    }

    try {
      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        client_id: formData.client_id || null,
        deadline: formData.deadline || null,
        status: formData.status,
        billing_type: formData.billing_type,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        fixed_price: formData.fixed_price ? parseFloat(formData.fixed_price) : null,
        completion_percentage: formData.billing_type === "fixed" ? formData.completion_percentage : null,
      };

      if (syncPayments && formData.billing_type === "fixed") {
        await updateProjectWithPayments.mutateAsync({
          id,
          project: projectData,
          paymentSetup,
          syncPayments: true,
        });
      } else {
        await updateProject.mutateAsync({
          id,
          ...projectData,
        } as any);
        toast({
          title: "Projeto atualizado!",
          description: "As alterações foram salvas com sucesso.",
        });
      }
      setIsEditDialogOpen(false);
    } catch (error: any) {
      // Error handled by mutation
    }
  };

  const handleUpdateCompletionPercentage = async (value: number) => {
    if (!id) return;
    try {
      await updateProject.mutateAsync({
        id,
        completion_percentage: value,
      } as any);
      toast({
        title: "Progresso atualizado",
        description: `Conclusão: ${value}%`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o progresso.",
      });
    }
  };

  const handleDeleteProject = async () => {
    if (!id) return;
    try {
      await deleteProject.mutateAsync(id);
      toast({
        title: "Projeto excluído",
        description: "O projeto e todos os dados relacionados foram removidos.",
      });
      navigate("/projects");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao excluir",
        description: error?.message || "Não foi possível excluir o projeto.",
      });
    }
  };


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter time entries for this project
  const projectTimeEntries = useMemo(() => {
    return allTimeEntries?.filter((entry) => entry.project_id === id) || [];
  }, [allTimeEntries, id]);

  // Use project time stats hook for billable/non-billable breakdown
  const projectTimeStats = useProjectTimeStats(
    allTimeEntries,
    id || "",
    project?.hourly_rate || profile?.hourly_rate || 0
  );

  // Calculate total hours from time entries
  const totalHoursSpent = useMemo(() => {
    return projectTimeEntries.reduce((acc, entry) => acc + ((entry.duration_minutes || 0) / 60), 0);
  }, [projectTimeEntries]);

  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, Task[]> = {
      todo: [],
      doing: [],
      done: [],
    };

    tasks?.forEach((task) => {
      const status = task.status || "todo";
      if (grouped[status]) {
        grouped[status].push(task);
      }
    });

    return grouped;
  }, [tasks]);

  const handleAddTask = async (status: string = "todo") => {
    if (!newTask.trim() || !id || !user?.id) return;

    try {
      await createTask.mutateAsync({
        project_id: id,
        user_id: user.id,
        title: newTask.trim(),
        status: status,
      });
      setNewTask("");
      setAddingToColumn(null);
      toast({
        title: "Tarefa adicionada!",
        description: "A tarefa foi adicionada ao projeto.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível adicionar a tarefa.",
      });
    }
  };

  const handleToggleComplete = async (taskId: string, currentStatus: string) => {
    if (!id) return;
    const newStatus = currentStatus === "done" ? "todo" : "done";
    try {
      await updateTask.mutateAsync({
        id: taskId,
        project_id: id,
        status: newStatus,
        completed: newStatus === "done",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a tarefa.",
      });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const taskId = event.active.id as string;
    const task = tasks?.find((t) => t.id === taskId);
    setActiveTask(task || null);
  };

  // Custom collision detection that prioritizes columns
  const taskCollisionDetectionStrategy: CollisionDetection = (args) => {
    // First, check for pointer collision with droppable columns
    const pointerCollisions = pointerWithin(args);
    
    // Find if we're over a column
    const columnIds = taskColumns.map(c => c.id);
    const overColumn = pointerCollisions.find(
      collision => columnIds.includes(collision.id as string)
    );
    
    if (overColumn) {
      return [overColumn];
    }

    // If not over a column directly, check which column the pointer is in
    const rectCollisions = rectIntersection(args);
    const columnCollision = rectCollisions.find(
      collision => columnIds.includes(collision.id as string)
    );
    
    if (columnCollision) {
      return [columnCollision];
    }

    // Fallback to closest center for items
    return closestCenter(args);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over || !id) return;

    const taskId = active.id as string;
    let newStatus: string | null = null;

    // Check if dropped directly on a column
    if (taskColumns.find((col) => col.id === over.id)) {
      newStatus = over.id as string;
    } else {
      // Dropped on another task card - find which column it belongs to
      const overTask = tasks?.find((t) => t.id === over.id);
      if (overTask) {
        newStatus = overTask.status || "todo";
      }
    }

    if (!newStatus) return;

    const task = tasks?.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;

    try {
      await updateTask.mutateAsync({
        id: taskId,
        project_id: id,
        status: newStatus,
        completed: newStatus === "done",
      });
      toast({
        title: "Tarefa atualizada",
        description: `Movida para ${taskColumns.find((c) => c.id === newStatus)?.title}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a tarefa.",
      });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!id) return;
    try {
      await deleteTask.mutateAsync({ id: taskId, project_id: id });
      toast({
        title: "Tarefa removida",
        description: "A tarefa foi removida do projeto.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível remover a tarefa.",
      });
    }
  };

  if (projectLoading) {
    return (
      <DashboardLayout title="Detalhes do Projeto">
        <div className="space-y-6">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-48" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout title="Projeto não encontrado">
        <div className="text-center py-12">
          <p className="text-muted-foreground">O projeto solicitado não foi encontrado.</p>
          <Link to="/projects">
            <Button className="mt-4">Voltar para Projetos</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const client = project.clients as { id: string; name: string; email: string } | null;
  const completedTasks = tasks?.filter((t) => t.status === "done").length || 0;
  const totalTasks = tasks?.length || 0;
  const taskProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  // Project billing info
  const billingType = ((project as any).billing_type as BillingType) || "hourly";
  const fixedPrice = (project as any).fixed_price || 0;
  const completionPercentage = (project as any).completion_percentage || 0;
  const estimatedHours = project.estimated_hours || 0;
  const hourlyRate = project.hourly_rate || profile?.hourly_rate || 0;
  
  // Calculations
  const hourlyProgress = estimatedHours > 0 ? (totalHoursSpent / estimatedHours) * 100 : 0;
  const billedAmount = totalHoursSpent * hourlyRate;
  const estimatedValue = estimatedHours * hourlyRate;
  const realHourlyRate = totalHoursSpent > 0 ? (billingType === "fixed" ? fixedPrice / totalHoursSpent : hourlyRate) : 0;
  const isProfitable = realHourlyRate >= (profile?.hourly_rate || 50);
  
  // Check deadline - don't show overdue for completed projects
  const daysUntilDeadline = project.deadline ? differenceInDays(new Date(project.deadline), new Date()) : null;
  const isProjectDone = project.status === 'done';
  const isDeadlineNear = !isProjectDone && daysUntilDeadline !== null && daysUntilDeadline >= 0 && daysUntilDeadline <= 7;
  const isOverdue = !isProjectDone && daysUntilDeadline !== null && daysUntilDeadline < 0;

  const formatDeadline = (deadline: string | null) => {
    if (!deadline) return "Sem prazo";
    try {
      return format(new Date(deadline), "dd 'de' MMMM, yyyy", { locale: ptBR });
    } catch {
      return "Data inválida";
    }
  };

  return (
    <DashboardLayout title="Detalhes do Projeto">
      <div className="space-y-6">
        {/* Back Link */}
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para projetos
        </Link>

        {/* Project Header */}
        <Card className="shadow-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl sm:text-2xl font-bold">{project.name}</h2>
                  <Badge className={statusColors[project.status || "backlog"]}>
                    {statusLabels[project.status || "backlog"]}
                  </Badge>
                  <Badge variant="outline" className={cn(
                    billingType === "fixed" 
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300" 
                      : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300"
                  )}>
                    {billingType === "fixed" ? "💰 Preço Fixo" : "⏱️ Por Hora"}
                  </Badge>
                </div>
                <p className="text-muted-foreground">{project.description || "Sem descrição"}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>{client?.name || "Sem cliente"}</span>
                  </div>
                  <div className={cn(
                    "flex items-center gap-2",
                    isOverdue ? "text-destructive" : isDeadlineNear ? "text-amber-600" : "text-muted-foreground"
                  )}>
                    <Calendar className="h-4 w-4" />
                    <span>{formatDeadline(project.deadline)}</span>
                    {isOverdue && <Badge variant="destructive" className="text-xs">Atrasado</Badge>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditDialogOpen(true)}
                    className="gap-2"
                  >
                    <Pencil className="h-4 w-4" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                    Excluir
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards - Adaptive based on billing type */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {billingType === "fixed" ? (
            <>
              <Card className="shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Valor do Projeto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(fixedPrice)}
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <span className="font-medium">{completionPercentage}%</span>
                    </div>
                    <Slider
                      value={[completionPercentage]}
                      onValueChange={([value]) => handleUpdateCompletionPercentage(value)}
                      max={100}
                      step={5}
                      className="cursor-pointer"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Horas Gastas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{Math.round(totalHoursSpent * 10) / 10}h</p>
                  {estimatedHours > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Estimado: {estimatedHours}h
                    </p>
                  )}
                  {(projectTimeStats.billableHours > 0 || projectTimeStats.nonBillableHours > 0) && (
                    <div className="mt-2 pt-2 border-t border-border/50 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-success">Faturável</span>
                        <span className="font-medium">{projectTimeStats.billableHours}h</span>
                      </div>
                      {projectTimeStats.nonBillableHours > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Não faturável</span>
                          <span className="font-medium">{projectTimeStats.nonBillableHours}h</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Taxa Real
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {totalHoursSpent > 0 ? (
                    <>
                      <p className={cn(
                        "text-2xl font-bold",
                        isProfitable ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
                      )}>
                        {formatCurrency(realHourlyRate)}/h
                      </p>
                      {!isProfitable && (
                        <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                          <AlertTriangle className="h-3 w-3" />
                          Abaixo da meta
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground">Sem horas registradas</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Horas Gastas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">
                    {Math.round(totalHoursSpent * 10) / 10}h 
                    <span className="text-lg text-muted-foreground font-normal"> de {estimatedHours}h</span>
                  </p>
                  <div className="mt-3 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progresso</span>
                      <span className="font-medium">{Math.round(hourlyProgress)}%</span>
                    </div>
                    <Progress 
                      value={Math.min(hourlyProgress, 100)} 
                      className={cn("h-2", hourlyProgress > 100 && "bg-destructive/20")}
                    />
                    {hourlyProgress > 100 && (
                      <p className="text-xs text-destructive flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Ultrapassou estimativa em {Math.round(hourlyProgress - 100)}%
                      </p>
                    )}
                  </div>
                  {(projectTimeStats.billableHours > 0 || projectTimeStats.nonBillableHours > 0) && (
                    <div className="mt-3 pt-2 border-t border-border/50 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-success">Faturável</span>
                        <span className="font-medium">{projectTimeStats.billableHours}h = {formatCurrency(projectTimeStats.billableValue)}</span>
                      </div>
                      {projectTimeStats.nonBillableHours > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Não faturável</span>
                          <span className="font-medium">{projectTimeStats.nonBillableHours}h</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Valor/Hora
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{formatCurrency(hourlyRate)}</p>
                </CardContent>
              </Card>
              <Card className="shadow-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Faturado
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(billedAmount)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Estimado: {formatCurrency(estimatedValue)}
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Alerts */}
        {billingType === "fixed" && totalHoursSpent > 0 && realHourlyRate < 50 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">Atenção: Rentabilidade baixa</p>
            </div>
            <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
              A taxa real deste projeto ({formatCurrency(realHourlyRate)}/h) está abaixo do recomendado. 
              Considere revisar o escopo ou ajustar preços para projetos futuros.
            </p>
          </div>
        )}

        {billingType === "hourly" && hourlyProgress > 100 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">Atenção: Horas estimadas ultrapassadas</p>
            </div>
            <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
              Este projeto já ultrapassou as horas estimadas em {Math.round(totalHoursSpent - estimatedHours)}h.
              Considere comunicar o cliente sobre a extensão do escopo.
            </p>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="overview">
          <TabsList className="w-full sm:w-auto flex-wrap h-auto">
            <TabsTrigger value="overview" className="flex-1 sm:flex-none">Visão Geral</TabsTrigger>
            <TabsTrigger value="tasks" className="flex-1 sm:flex-none">Tarefas</TabsTrigger>
            <TabsTrigger value="time" className="flex-1 sm:flex-none">Tempo</TabsTrigger>
            <TabsTrigger value="financial" className="flex-1 sm:flex-none">Financeiro</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6 space-y-6">
            {/* Quick Actions */}
            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Iniciar Trabalho</h3>
                    <p className="text-sm text-muted-foreground">Comece a rastrear tempo neste projeto</p>
                  </div>
                  <Link to="/timer">
                    <Button className="gap-2">
                      <Play className="h-4 w-4" />
                      Iniciar Timer
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Task Summary */}
            {totalTasks > 0 && (
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Progresso das Tarefas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{completedTasks} de {totalTasks} tarefas concluídas</span>
                      <span className="font-medium">{taskProgress}%</span>
                    </div>
                    <Progress value={taskProgress} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="tasks" className="mt-6">
            {/* Task Kanban */}
            {tasksLoading ? (
              <div className="grid gap-4 md:grid-cols-3">
                {taskColumns.map((col) => (
                  <div key={col.id} className="space-y-4">
                    <Skeleton className="h-8 w-32" />
                    <Skeleton className="h-20" />
                    <Skeleton className="h-20" />
                  </div>
                ))}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={taskCollisionDetectionStrategy}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="grid gap-4 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {taskColumns.map((column) => {
                    const columnTasks = tasksByStatus[column.id] || [];
                    const isAddingHere = addingToColumn === column.id;
                    return (
                      <KanbanColumn
                        key={column.id}
                        id={column.id}
                        title={column.title}
                        count={columnTasks.length}
                        items={columnTasks.map((t) => t.id)}
                        variant="task"
                        onAddClick={() => setAddingToColumn(column.id)}
                      >
                        {/* Inline Add Task Input */}
                        {isAddingHere && (
                          <div className="space-y-2">
                            <Input
                              autoFocus
                              value={newTask}
                              onChange={(e) => setNewTask(e.target.value)}
                              placeholder="Nome da tarefa..."
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleAddTask(column.id);
                                if (e.key === "Escape") {
                                  setAddingToColumn(null);
                                  setNewTask("");
                                }
                              }}
                              className="bg-card"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleAddTask(column.id)}
                                disabled={createTask.isPending || !newTask.trim()}
                                className="flex-1"
                              >
                                Adicionar
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setAddingToColumn(null);
                                  setNewTask("");
                                }}
                              >
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {columnTasks.map((task) => (
                          <SortableTaskCard
                            key={task.id}
                            id={task.id}
                            title={task.title}
                            description={task.description}
                            status={task.status || "todo"}
                            onDelete={() => handleDeleteTask(task.id)}
                            onToggleComplete={() => handleToggleComplete(task.id, task.status || "todo")}
                          />
                        ))}
                        
                        {columnTasks.length === 0 && !isAddingHere && (
                          <div className="flex h-16 items-center justify-center text-sm text-muted-foreground">
                            Sem tarefas
                          </div>
                        )}
                      </KanbanColumn>
                    );
                  })}
                </div>

                <DragOverlay>
                  {activeTask && (
                    <TaskDragOverlay
                      title={activeTask.title}
                      description={activeTask.description}
                      status={activeTask.status || "todo"}
                    />
                  )}
                </DragOverlay>
              </DndContext>
            )}
          </TabsContent>

          <TabsContent value="time" className="mt-6 space-y-4">
            {/* Manual Time Entry */}
            <ProjectManualTimeEntry 
              projectId={id!} 
              projectHourlyRate={project.hourly_rate} 
            />

            <Card className="shadow-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Histórico de Tempo</CardTitle>
                  <div className="text-sm text-muted-foreground">
                    Total: <span className="font-semibold text-foreground">{Math.round(totalHoursSpent * 10) / 10}h</span>
                    {billingType === "hourly" && (
                      <> = <span className="font-semibold text-emerald-600">{formatCurrency(billedAmount)}</span></>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {projectTimeEntries.length > 0 ? (
                  <div className="space-y-4">
                    {projectTimeEntries.map((entry) => {
                      const hours = Math.floor((entry.duration_minutes || 0) / 60);
                      const mins = (entry.duration_minutes || 0) % 60;
                      const entryValue = ((entry.duration_minutes || 0) / 60) * (entry.hourly_rate || hourlyRate);
                      return (
                        <div
                          key={entry.id}
                          className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b pb-4 last:border-0 last:pb-0"
                        >
                          <div className="space-y-1">
                            <p className="font-medium">{entry.description || "Sem descrição"}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(entry.start_time), {
                                addSuffix: true,
                                locale: ptBR,
                              })}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-primary">
                              {hours > 0 ? `${hours}h ` : ""}
                              {mins}min
                            </span>
                            {billingType === "hourly" && entryValue > 0 && (
                              <span className="text-sm text-emerald-600">{formatCurrency(entryValue)}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhum tempo registrado para este projeto.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="financial" className="mt-6 space-y-6">
            {billingType === "fixed" ? (
              <>
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg">Análise de Rentabilidade</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">Valor do Projeto</p>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(fixedPrice)}</p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">Horas Investidas</p>
                        <p className="text-2xl font-bold">{Math.round(totalHoursSpent * 10) / 10}h</p>
                      </div>
                    </div>
                    
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground mb-2">Se fosse cobrado por hora</p>
                      <p className="text-xl font-semibold">
                        {formatCurrency(totalHoursSpent * (profile?.hourly_rate || hourlyRate))}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ({Math.round(totalHoursSpent * 10) / 10}h × {formatCurrency(profile?.hourly_rate || hourlyRate)}/h)
                      </p>
                      {fixedPrice > totalHoursSpent * (profile?.hourly_rate || hourlyRate) ? (
                        <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                          +{formatCurrency(fixedPrice - totalHoursSpent * (profile?.hourly_rate || hourlyRate))} de lucro extra
                        </Badge>
                      ) : totalHoursSpent > 0 && (
                        <Badge className="mt-2 bg-amber-100 text-amber-700">
                          {formatCurrency(fixedPrice - totalHoursSpent * (profile?.hourly_rate || hourlyRate))} abaixo
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg">Resumo Financeiro</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">Faturado</p>
                        <p className="text-2xl font-bold text-emerald-600">{formatCurrency(billedAmount)}</p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">Estimado</p>
                        <p className="text-2xl font-bold">{formatCurrency(estimatedValue)}</p>
                      </div>
                      <div className="rounded-lg border p-4">
                        <p className="text-sm text-muted-foreground">Variação</p>
                        <p className={cn(
                          "text-2xl font-bold",
                          billedAmount > estimatedValue ? "text-emerald-600" : "text-amber-600"
                        )}>
                          {billedAmount > estimatedValue ? "+" : ""}{formatCurrency(billedAmount - estimatedValue)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="rounded-lg border p-4">
                      <p className="text-sm text-muted-foreground mb-2">Projeção de Conclusão</p>
                      {estimatedHours > 0 && totalHoursSpent > 0 && (
                        <>
                          <p className="text-lg font-medium">
                            {Math.round(hourlyProgress)}% do tempo estimado utilizado
                          </p>
                          {hourlyProgress > 100 ? (
                            <p className="text-sm text-amber-600">
                              Projeto ultrapassou a estimativa em {Math.round(totalHoursSpent - estimatedHours)}h
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              Restam aproximadamente {Math.round(estimatedHours - totalHoursSpent)}h estimadas
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
            
            {/* Payments Section */}
            <ProjectPaymentsSection
              projectId={id || ""}
              projectName={project.name}
              projectValue={billingType === "fixed" ? fixedPrice : null}
              billingType={billingType}
            />
          </TabsContent>
        </Tabs>

        {/* Edit Project Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Projeto</DialogTitle>
              <DialogDescription>
                Atualize as informações do projeto
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome do Projeto *</Label>
                <Input
                  id="edit-name"
                  placeholder="Ex: Website Redesign"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Descrição</Label>
                <Textarea
                  id="edit-description"
                  placeholder="Descreva o projeto..."
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="edit-client">Cliente</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, client_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-deadline">Prazo</Label>
                  <Input
                    id="edit-deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData((prev) => ({ ...prev, deadline: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value as ProjectStatus }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        {opt.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Billing Type Selection */}
              <div className="space-y-3">
                <Label>Tipo de Cobrança *</Label>
                <RadioGroup
                  value={formData.billing_type}
                  onValueChange={(value: BillingType) => setFormData((prev) => ({ ...prev, billing_type: value }))}
                  className="grid gap-3"
                >
                  <div
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                      formData.billing_type === "hourly" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                    )}
                    onClick={() => setFormData((prev) => ({ ...prev, billing_type: "hourly" }))}
                  >
                    <RadioGroupItem value="hourly" id="edit-hourly" className="mt-0.5" />
                    <div className="flex-1">
                      <label htmlFor="edit-hourly" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Por Hora
                      </label>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors",
                      formData.billing_type === "fixed" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                    )}
                    onClick={() => setFormData((prev) => ({ ...prev, billing_type: "fixed" }))}
                  >
                    <RadioGroupItem value="fixed" id="edit-fixed" className="mt-0.5" />
                    <div className="flex-1">
                      <label htmlFor="edit-fixed" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Preço Fixo
                      </label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Conditional Fields based on billing type */}
              {formData.billing_type === "hourly" ? (
                <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="edit-hourly_rate">Valor/Hora (R$)</Label>
                      <Input
                        id="edit-hourly_rate"
                        type="number"
                        placeholder="100.00"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hourly_rate: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-estimated_hours">Horas Estimadas *</Label>
                      <Input
                        id="edit-estimated_hours"
                        type="number"
                        placeholder="40"
                        value={formData.estimated_hours}
                        onChange={(e) => setFormData((prev) => ({ ...prev, estimated_hours: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                  <div className="space-y-2">
                    <Label htmlFor="edit-fixed_price">Valor do Projeto (R$) *</Label>
                    <Input
                      id="edit-fixed_price"
                      type="number"
                      placeholder="5000.00"
                      value={formData.fixed_price}
                      onChange={(e) => setFormData((prev) => ({ ...prev, fixed_price: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-estimated_hours_fixed">Horas Estimadas (opcional)</Label>
                    <Input
                      id="edit-estimated_hours_fixed"
                      type="number"
                      placeholder="50"
                      value={formData.estimated_hours}
                      onChange={(e) => setFormData((prev) => ({ ...prev, estimated_hours: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Percentual de Conclusão</Label>
                      <span className="text-sm font-medium">{formData.completion_percentage}%</span>
                    </div>
                    <Slider
                      value={[formData.completion_percentage]}
                      onValueChange={([value]) => setFormData((prev) => ({ ...prev, completion_percentage: value }))}
                      max={100}
                      step={5}
                    />
                  </div>
                </div>
              )}

              {/* Sync Payments Section - Only for fixed price projects */}
              {formData.billing_type === "fixed" && (
                <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label className="font-medium">Sincronizar Pagamentos</Label>
                      <p className="text-xs text-muted-foreground">
                        {hasPendingPayments 
                          ? "Substituir pagamentos pendentes com nova configuração"
                          : "Criar novos pagamentos para este projeto"
                        }
                      </p>
                    </div>
                    <Switch
                      checked={syncPayments}
                      onCheckedChange={setSyncPayments}
                    />
                  </div>

                  {syncPayments && (
                    <>
                      <Separator />
                      <PaymentSetupSection
                        projectValue={parseFloat(formData.fixed_price) || 0}
                        billingType="fixed"
                        value={paymentSetup}
                        onChange={setPaymentSetup}
                        disabled={updateProject.isPending || updateProjectWithPayments.isPending}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="w-full sm:w-auto">
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveProject} 
                disabled={updateProject.isPending || updateProjectWithPayments.isPending} 
                className="w-full sm:w-auto"
              >
                {(updateProject.isPending || updateProjectWithPayments.isPending) ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir Projeto</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o projeto <strong>"{project.name}"</strong>? 
                Esta ação não pode ser desfeita e irá remover também:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Todas as tarefas do projeto</li>
                  <li>Todos os registros de tempo</li>
                  <li>Todos os pagamentos associados</li>
                </ul>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteProject}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteProject.isPending ? "Excluindo..." : "Excluir Projeto"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
